import type { Gltf } from '../gltf';

/**
 * Find a named empty/socket node in a glTF and return its local transform.
 * Sockets are authored as named glTF nodes (typically empties in Blender) —
 * scene composites use them to anchor the light/mesh slot to a meaningful
 * point on the body, e.g. a "wick" empty inside a lantern.
 *
 * Returns null when no node with that name exists.
 *
 * Takes a plain `{ json: Gltf }` rather than a full `ParsedModel` so both the
 * runtime path (via `scene.modelRegistry`) and the editor preview path (via
 * `GlbParserService.getParsed`) can share the same socket lookup.
 *
 * **Limitation:** uses the node's raw `translation` / `rotation` without
 * composing parent-node transforms or `_MODEL_TRANSFORM` extras. For simple
 * GLBs where the socket node is a sibling of the mesh under an identity
 * model transform, this matches the visual position. Models that rely on
 * `_MODEL_TRANSFORM` scale (e.g. Sponza's 0.01) will need the socket
 * coordinates authored in the pre-transform space, or set `localPosition`
 * directly instead.
 */
export function resolveSocket(
  model: { json: Gltf },
  socketName: string,
): { translation: [number, number, number]; rotation: [number, number, number, number] } | null {
  const node = model.json.nodes?.find((n) => n.name === socketName);
  if (!node) return null;
  const t = node.translation ?? [0, 0, 0];
  const r = node.rotation ?? [0, 0, 0, 1];
  return {
    translation: [t[0], t[1], t[2]],
    rotation: [r[0], r[1], r[2], r[3]],
  };
}
